/**
 * Activation funnel: signup → readiness check → first MCQs → returns → Pro.
 * The diagnostic the user asked for: "is our problem activation or the offer?"
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/App";
import { Loader2, Filter, Compass } from "lucide-react";
import { toast } from "sonner";

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

const AdminFunnelPage = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get(`/admin/funnel?days=${days}`));
    } catch {
      toast.error("Failed to load funnel");
    } finally {
      setLoading(false);
    }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const steps = data?.steps || [];
  const signups = steps[0]?.count || 0;

  return (
    <div className="space-y-6" data-testid="admin-funnel-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Filter className="w-6 h-6 text-primary" /> Activation funnel</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Signups in the selected window and how far they got. If "10+ MCQs in 24h" is low, the problem is activation. If it's high but Pro is low, the problem is the offer.
          </p>
        </div>
        <div className="flex gap-1 rounded-full border border-border p-1 bg-card">
          {RANGES.map((r) => (
            <button
              key={r.days}
              data-testid={`funnel-range-${r.days}`}
              onClick={() => setDays(r.days)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${days === r.days ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
          {signups === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">No signups in the last {days} days.</p>
          )}
          {signups > 0 && steps.map((s, i) => {
            const prev = i > 0 ? steps[i - 1] : null;
            const stepConv = prev && prev.count > 0 ? Math.round((s.count / prev.count) * 100) : null;
            return (
              <div key={s.key} data-testid={`funnel-step-${s.key}`}>
                <div className="flex items-baseline justify-between mb-1.5 gap-2">
                  <span className="text-sm font-medium">{s.label}</span>
                  <span className="text-sm tabular-nums">
                    <strong>{s.count}</strong>
                    <span className="text-muted-foreground"> · {s.pct}% of signups</span>
                    {stepConv !== null && (
                      <span className={`ml-2 text-xs ${stepConv >= 50 ? "text-success" : stepConv >= 25 ? "text-warning" : "text-destructive"}`}>
                        {stepConv}% from previous
                      </span>
                    )}
                  </span>
                </div>
                <div className="h-3 rounded-full bg-secondary overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${i === steps.length - 1 ? "bg-success" : "bg-primary"}`}
                    style={{ width: `${Math.max(s.pct, s.count > 0 ? 2 : 0)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TourImpactCard days={days} />

      <div className="rounded-xl border border-border bg-secondary/30 p-4 text-xs text-muted-foreground space-y-1">
        <p><strong>How to read this:</strong></p>
        <p>• "10+ MCQs in 24h" under ~30% → activation problem: improve /welcome flow and Day-0/1 emails.</p>
        <p>• Healthy activation but "Purchased Pro" near 0 → offer/paywall problem: revisit pricing page and trial-end messaging.</p>
        <p>• "Returned next day" is the strongest early predictor of eventual purchase - watch it week over week.</p>
      </div>
    </div>
  );
};

/** Does the first-login spotlight tour actually change behaviour? */
const TourImpactCard = ({ days }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/tour-impact?days=${days}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const cohorts = data?.cohorts || [];
  const finished = cohorts.find((c) => c.key === "completed");
  const never = cohorts.find((c) => c.key === "never");
  const lift = finished && never && finished.users >= 5 && never.users >= 5 && never.avg_questions > 0
    ? Math.round(((finished.avg_questions - never.avg_questions) / never.avg_questions) * 100)
    : null;

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="tour-impact-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Compass className="w-5 h-5 text-primary" /> Product tour impact
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Signups in the last {days} days, split by how far they got through the first-login tour.
          </p>
        </div>
        {lift !== null && (
          <span
            data-testid="tour-impact-lift"
            className={`text-sm font-semibold whitespace-nowrap ${lift >= 0 ? "text-success" : "text-destructive"}`}
          >
            {lift >= 0 ? "+" : ""}{lift}% questions vs no tour
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : !cohorts.length ? (
        <p className="text-sm text-muted-foreground py-6 text-center">No signups in this window.</p>
      ) : (
        <>
          {!data?.enough_data && (
            <p className="text-xs text-warning mb-3" data-testid="tour-impact-warning">
              Not enough data for a verdict yet — the tour shipped on 11 Sep 2026. Treat these as directional until each
              cohort has 10+ members.
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="text-xs text-muted-foreground text-left">
                  <th className="py-2 pr-3 font-medium">Cohort</th>
                  <th className="py-2 px-3 font-medium text-right">Members</th>
                  <th className="py-2 px-3 font-medium text-right">Avg questions</th>
                  <th className="py-2 px-3 font-medium text-right">Reached 10 MCQs</th>
                  <th className="py-2 px-3 font-medium text-right">Returned next day</th>
                  <th className="py-2 pl-3 font-medium text-right">Paid Pro</th>
                </tr>
              </thead>
              <tbody>
                {cohorts.map((c) => (
                  <tr key={c.key} className="border-t border-border" data-testid={`tour-cohort-${c.key}`}>
                    <td className="py-2.5 pr-3 font-medium">{c.label}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums">{c.users}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums">{c.avg_questions}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums">{c.reached_10_pct}%</td>
                    <td className="py-2.5 px-3 text-right tabular-nums">{c.returned_next_day_pct}%</td>
                    <td className="py-2.5 pl-3 text-right tabular-nums">{c.paid_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminFunnelPage;
